// Copyright 2017-2026 @polkadot/apps authors & contributors
// SPDX-License-Identifier: Apache-2.0

import type { HexString } from '@polkadot/util/types';

import { getCachedG1GenesisHash, setCachedG1GenesisHash, validateGenesisHash } from '@polkadot/apps-config/api/g1Genesis';

const G1_GENESIS_URL = 'https://get-g1-genesis-hash.p2p.legal/';
const FETCH_TIMEOUT = 5000;

export async function fetchG1GenesisHash (): Promise<HexString | null> {
  const cached = getCachedG1GenesisHash();

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    const response = await fetch(G1_GENESIS_URL, { signal: controller.signal });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.warn(`[g1Genesis] HTTP ${response.status} from genesis endpoint`);

      return cached;
    }

    const data = await response.json() as { hash?: string };
    const rawHash = data.hash || '';

    if (!rawHash) {
      console.log('[g1Genesis] G1 not yet active (empty hash)');

      // Genesis hash is immutable: keep cache if it exists
      return cached;
    }

    const validHash = validateGenesisHash(rawHash);

    if (validHash) {
      setCachedG1GenesisHash(validHash);
      console.log(`[g1Genesis] G1 genesis hash: ${validHash}`);

      return validHash;
    }

    console.warn(`[g1Genesis] Invalid hash format: ${rawHash}`);

    return cached;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.warn('[g1Genesis] Fetch timed out');
    } else {
      console.warn('[g1Genesis] Fetch failed:', error);
    }

    return cached;
  }
}
